<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import type { Services } from "@services"
import { usePvStore } from "@modules/pv/stores/pv.store"
import { format as d3Format } from "d3-format"
import { formatDate, formatNumber } from "@core/helpers"
import type { BaseKpiPassThrough } from "@core/components/base/kpi/baseKpiPassThrough"
import type {
    PvValorisationTemplateRoot,
    ValorisationPvAnalysis,
} from "@modules/pv/types/valorisation-pv.types"
import { useFetchWrapper } from "@core/composables"
import { useChartExport } from "@modules/pv/composables/useChartExport"

// ================================
// TYPES & INTERFACES
// ================================

/** Graphique Plotly prêt pour {@link PlotlyChart}. */
type PlotlyGraphPayload = {
    data: object[]
    layout: Record<string, unknown>
}

type EcoTableRow = {
    opex: string
    elecsoutirage_sans_yearlymean: string
    elecsoutirage_avec_yearlymean: string
    diff_elect_tout_yearlymean: string
    prixHT_elecinjection_avec_yearlymean: string
    fluxHT_sans_yearlymean: string
    fluxHT_avec_yearlymean: string
    cashflowHT_yearlymean: string
}

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    /** Document d’analyse valorisation (données + graphiques). */
    analyse: ValorisationPvAnalysis
    /**
     * Gabarit racine renvoyé par l’API (`lastEtudeEco.template`), pour les formats
     * des champs `data.*` (équivalent legacy `templateAnalyse`).
     */
    ecoTemplate?: PvValorisationTemplateRoot | Record<string, unknown> | null
    /** Bâtiment courant (suppression optimiste + rollback si échec API). */
    buildingId?: string | null
    /** Source PDL courante. */
    sourceId?: string | null
}>()

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            dateLine: "Réalisée le {date}",
            reportCta: "Télécharger le rapport",
            deleteTooltip: "Supprimer l’étude",
            confirmDeleteTitle: "Supprimer l’analyse",
            confirmDeleteMessage:
                "Supprimer définitivement l’étude économique « {name} » ?",
            confirmAccept: "Supprimer",
            confirmReject: "Annuler",
            toastDeleteSuccess: "Analyse supprimée",
            toastDeleteError: "La suppression a échoué",
            toastDownloadError: "Impossible de générer le rapport PDF",
            exportFinancialSynthesis: "Synthèse financière (Excel)",
            titles: {
                overview: "Aperçu financier",
                overviewPopup:
                    "Une synthèse des aspects monétaires et temporels du projet, incluant les coûts, les revenus, les bénéfices et les projections financières sur la durée de vie du projet. C'est une vue d'ensemble concise mais informative des éléments clés permettant d'évaluer la santé financière et la viabilité de votre investissement.",
                financialImpact:
                    "Moyenne de l’Impact Financier Annuel des Panneaux Solaires",
                financialImpactPopup:
                    "Cela englobe les économies réalisées sur les factures d'électricité, les revenus provenant de la vente de l'électricité excédentaire, ainsi que les coûts d'installation et d'entretien des panneaux solaires. En résumé, c'est une évaluation de l'impact financier annuel net des panneaux solaires sur le budget énergétique d'un ménage ou d'une entreprise.",
                cashflow: "Cashflow annuel",
                cashflowPopup:
                    "Le cash flow annuel est la différence entre l'argent entrant et sortant chaque année pendant la durée de vie du projet, comprenant les revenus de vente d'électricité, les économies sur les factures d'électricité et les coûts d'entretien des panneaux solaires.",
                evolutionBill: "Évolution de la facture annuelle énergétique",
                evolutionBillPopup:
                    "C'est la variation des dépenses annuelles liées à l'énergie. Cela inclut les changements avant et après l'installation, en tenant compte de la vente ou de l'injection d'électricité dans le réseau, ainsi que des coûts initiaux d'investissement.",
            },
            kpis: {
                investissement: "Investissement initial",
                bilan: "Profit net avant amortissement de l'investissement",
                van: "VAN",
                vanSub: "Valeur actuelle nette",
                vanPopup:
                    "Elle mesure la rentabilité d'un investissement en soustrayant les coûts initiaux des bénéfices futurs, ajustés pour refléter leur valeur actuelle. Une VAN positive signifie que l'investissement est rentable.",
                rsi: "RSI",
                rsiSub: "Retour sur investissement",
                rsiPopup:
                    "ROI en anglais, c’est une mesure de la rentabilité d'un investissement. Il représente le rapport entre le bénéfice net généré par un investissement et le coût initial de cet investissement, exprimé en pourcentage. Un RSI positif indique que l'investissement a généré des bénéfices, tandis qu'un RSI négatif indique une perte.",
                tri: "TRI",
                triSub: "Taux rendement interne",
                triPopup:
                    "C’est une mesure de rentabilité qui calcule le taux de rendement annualisé d'un investissement, c'est-à-dire le taux d'intérêt auquel la valeur actuelle nette (VAN) des flux de trésorerie d'un projet est égale à zéro. En d'autres termes, c'est le taux de croissance attendu de l'investissement sur sa durée de vie. Un TRI plus élevé indique une meilleure rentabilité.",
                payback: "Payback",
                paybackSub: "Temps de retour",
                paybackPopup:
                    "Le \"payback\" ou \"temps de retour\" est le délai nécessaire pour récupérer un investissement initial à travers les flux de trésorerie générés par un projet. C'est le temps qu'il faut pour que les bénéfices accumulés égalent le coût initial de l'investissement. Un temps de retour plus court est généralement préférable, car il signifie que l'investissement se rembourse plus rapidement.",
            },
            table: {
                consoWithoutPv: "Consommation sans installation PV",
                consoWithPv:
                    "Consommation avec installation en injection de surplus",
                opex: "Moyenne des dépenses annuelle sur la durée de vie du projet (OPEX) (provision onduleur inclus)",
                revenue:
                    "Moyenne annuelle des revenus de vente de l’énergie Injectée",
                bill: "Moyenne des factures d’énergie soutirée",
                bilan: "Bilan annuel moyen",
                averageEconomy:
                    "Moyenne des économies annuelles : Gains (+) et pertes (-)",
                gainsAutoconso: "gagnés grâce à l’autoconsommation",
                gainsRevenue:
                    "gagnés grâce au revenus de surplus d’électricité",
                gainsTotal: "gagnés au total",
            },
        },
    },
})

const services = inject<Services>("services")
if (!services) {
    throw new Error("PvEtudeEcoDisplay: services non injectés")
}

const { $downloadAnalyseEco, $deleteAnalyseEcoPvById } = services

const confirm = useConfirm()
const toast = useToast()
const pvStore = usePvStore()

const isDeletingEco = ref(false)
const { downloadWorkbookAsExcel } = useChartExport()

/** Affiche l’action supprimer uniquement avec bâtiment + source (page bâtiment). */
const getShowDeleteEtudeAction = computed(
    () =>
        typeof props.buildingId === "string" &&
        props.buildingId.length > 0 &&
        typeof props.sourceId === "string" &&
        props.sourceId.length > 0
)

const { fetch: onClickDownloadReport, isFetching: isDownloadingReport } =
    useFetchWrapper(async () => {
        try {
            const result = await $downloadAnalyseEco(props.analyse._id)
            const url = (result as { result?: { url?: string } })?.result?.url
            if (typeof url === "string" && url.length > 0) {
                window.open(url, "_blank", "noopener,noreferrer")
            } else {
                toast.add({
                    severity: "info",
                    summary: t("reportCta"),
                    detail: String(
                        (result as { status?: string })?.status ?? ""
                    ),
                    life: 5000,
                })
            }
        } catch {
            toast.add({
                severity: "error",
                summary: t("toastDownloadError"),
                life: 6000,
            })
        }
    })

function onClickOpenDeleteConfirm(): void {
    if (!getShowDeleteEtudeAction.value || isDeletingEco.value) return
    const bid = props.buildingId
    const sid = props.sourceId
    if (!bid || !sid) return

    const name =
        typeof props.analyse.name_analyse === "string" &&
        props.analyse.name_analyse.length > 0
            ? props.analyse.name_analyse
            : String(props.analyse._id)

    confirm.require({
        header: t("confirmDeleteTitle"),
        message: t("confirmDeleteMessage", { name }),
        icon: "pi pi-exclamation-triangle",
        rejectLabel: t("confirmReject"),
        acceptProps: {
            icon: "pi pi-trash",
            label: t("confirmAccept"),
            severity: "danger",
        },
        rejectProps: {
            label: t("confirmReject"),
            severity: "secondary",
            outlined: true,
        },
        accept: async () => {
            const id = props.analyse._id
            const snap = pvStore.snapshotPvListsAndLastStudies()
            pvStore.replaceLastEtudeEco(null)
            pvStore.removeAnalyseEcoFromHistory(id)
            isDeletingEco.value = true
            try {
                await $deleteAnalyseEcoPvById(id)
                toast.add({
                    severity: "success",
                    summary: t("toastDeleteSuccess"),
                    life: 4000,
                })
                try {
                    await pvStore.fetchLastEtudeEcoForSource({
                        sourceId: sid,
                        buildingId: bid,
                    })
                } catch {
                    /* état déjà cohérent après DELETE ; refetch best-effort */
                }
            } catch {
                pvStore.restorePvListsAndLastStudies(snap)
                toast.add({
                    severity: "error",
                    summary: t("toastDeleteError"),
                    life: 6000,
                })
            } finally {
                isDeletingEco.value = false
            }
        },
        reject: () => {},
    })
}

// ================================
// BUSINESS LOGIC — formatage & lecture API
// ================================

/**
 * Formate une valeur métier comme l’historique Nuxt (d3-format optionnel + repli FR).
 *
 * @param value - Nombre brut
 * @param formatting - Motif d3-format issu du gabarit API
 * @param digits - Décimales max en repli
 * @returns Chaîne affichable ou tiret
 */
function formatPvMetricValue(
    value: number | null | undefined,
    formatting: string | null | undefined,
    digits = 2
): string {
    if (value === undefined || value === null || Number.isNaN(Number(value))) {
        return "-"
    }
    const n = Number(value)
    if (formatting && formatting.length > 0) {
        try {
            return d3Format(formatting)(n)
        } catch {
            /* repli ci-dessous */
        }
    }
    return n.toLocaleString("fr-FR", { maximumFractionDigits: digits })
}

/**
 * Indique si une valeur affichée (nombre ou chaîne numérique) est strictement positive.
 *
 * @param value - Valeur issue du tableau formaté
 */
function getIsPositiveDisplay(value: string | undefined): boolean {
    if (value === undefined || value === "-") return false
    const normalized = String(value)
        .replace(/\u00a0/g, " ")
        .replace(/\s/g, "")
        .replace(",", ".")
    const n = Number(normalized)
    return !Number.isNaN(n) && n > 0
}

/**
 * Extrait le couple data/layout Plotly pour une clé du bloc `charts` (legacy ou type plotly).
 *
 * @param charts - Carte des graphiques renvoyée par l’API
 * @param chartKey - ex. `cashflowHTactualise`, `fluxHT`
 */
function getPlotlyGraphFromCharts(
    charts: unknown,
    chartKey: string
): PlotlyGraphPayload | null {
    if (!charts || typeof charts !== "object") return null
    const block = (charts as Record<string, unknown>)[chartKey]
    if (!block || typeof block !== "object") return null
    const b = block as Record<string, unknown>

    if (b.data && typeof b.data === "object") {
        const inner = b.data as Record<string, unknown>
        if (Array.isArray(inner.data)) {
            return {
                data: inner.data as object[],
                layout: (inner.layout as Record<string, unknown>) ?? {},
            }
        }
    }
    if (b.type === "plotly" && b.data && typeof b.data === "object") {
        const p = b.data as { data?: unknown; layout?: unknown }
        if (Array.isArray(p.data)) {
            return {
                data: p.data as object[],
                layout: (p.layout as Record<string, unknown>) ?? {},
            }
        }
    }
    return null
}

/**
 * Retourne le motif d3-format pour une clé `data.*` depuis le gabarit racine.
 *
 * @param key - Clé métier (ex. `prixHT_installation`)
 */
function getFormatForDataKey(key: string): string | undefined {
    const root = props.ecoTemplate as
        | PvValorisationTemplateRoot
        | null
        | undefined
    const fmt = root?.data?.[key]?.format
    return typeof fmt === "string" && fmt.length > 0 ? fmt : undefined
}

/**
 * Déduit le nombre de décimales max pour le tableau à partir d’un motif d3-format API.
 *
 * @param formatting - ex. `",.0f"`, `".1f"`
 */
function inferMaxFractionDigitsFromFormat(
    formatting: string | null | undefined
): number {
    if (!formatting) return 2
    const match = formatting.match(/\.(\d+)f/)
    if (match) {
        const n = parseInt(match[1], 10)
        if (!Number.isNaN(n)) return Math.min(8, Math.max(0, n))
    }
    return 2
}

/**
 * Formate une cellule du tableau d’impact financier avec {@link formatNumber} (locale fr-FR).
 *
 * @param key - Clé `data` / gabarit pour le pas décimal
 * @param value - Montant brut
 */
function formatTableMetricValue(
    key: string,
    value: number | null | undefined
): string {
    const maxFrac = inferMaxFractionDigitsFromFormat(getFormatForDataKey(key))
    return formatNumber(value, {
        style: "decimal",
        maximumFractionDigits: maxFrac,
        minimumFractionDigits: 0,
        defaultValue: "-",
    })
}

/**
 * Lit la valeur numérique `analyse.data[key].value`.
 *
 * @param key - Clé métier
 */
function getNumericDataValue(key: string): number | null | undefined {
    const cell = props.analyse.data?.[key]
    return cell?.value
}

/**
 * Classes PassThrough {@link BaseKPI} pour l’aperçu financier ; le KPI « bilan » en vert
 * si le cumul est ≥ 0, en rouge si strictement négatif.
 *
 * @param kpiType - Identifiant du KPI (`bilan`, `van`, …)
 */
function getEtudeEcoKpiPt(kpiType: string): BaseKpiPassThrough {
    const isBilan = kpiType === "bilan"
    const bilanRaw = isBilan
        ? getNumericDataValue("cashflowHTactualise_sanscapex_sum")
        : null
    const isBilanNegative =
        isBilan &&
        bilanRaw !== undefined &&
        bilanRaw !== null &&
        !Number.isNaN(Number(bilanRaw)) &&
        Number(bilanRaw) < 0

    let titleClass: string
    let valueClass: string
    if (!isBilan) {
        titleClass =
            "normal-case text-sm font-medium text-primary-600 flex flex-wrap items-center justify-center gap-1 text-center"
        valueClass = "text-xl font-semibold text-primary-900 tabular-nums"
    } else if (isBilanNegative) {
        titleClass =
            "normal-case text-sm font-medium !text-red-700 flex flex-wrap items-center justify-center gap-1 text-center"
        valueClass = "text-xl font-semibold !text-red-700 tabular-nums"
    } else {
        titleClass =
            "normal-case text-sm font-medium !text-emerald-700 flex flex-wrap items-center justify-center gap-1 text-center"
        valueClass = "text-xl font-semibold !text-emerald-800 tabular-nums"
    }

    return {
        root: "!max-w-none w-full min-h-0 h-auto min-w-0 rounded-md border border-surface-200 p-6 gap-4 bg-white shadow-none justify-start items-stretch mx-0 sm:max-w-none h-full",
        title: titleClass,
        valueRow:
            "flex flex-col w-full min-w-0 flex-1 justify-start items-center text-center gap-1 mt-1",
        value: valueClass,
    }
}

// ================================
// DONNÉES CALCULÉES
// ================================

/** Titre principal de l’analyse (nom ou identifiant). */
const getStudyHeading = computed(() => {
    const n = props.analyse.name_analyse
    return typeof n === "string" && n.length > 0 ? n : String(props.analyse._id)
})

/** Ligne « Réalisée le … » (date de création). */
const getDateLine = computed(() =>
    t("dateLine", {
        date: formatDate(props.analyse.createdAt, "DD/MM/YYYY"),
    })
)

/** KPI « bilan » : surbrillance verte si le cumul cashflow dépasse l’investissement. */
const getBilanHighlight = computed(() => {
    const invest = getNumericDataValue("prixHT_installation")
    const cash = getNumericDataValue("cashflowHTactualise_sanscapex_sum")
    if (
        invest === undefined ||
        invest === null ||
        cash === undefined ||
        cash === null
    ) {
        return false
    }
    return cash > invest
})

/** Grille des six KPI financiers (libellés et valeurs alignés legacy). */
const getKpiItems = computed(() => {
    const bilanHighlight = getBilanHighlight.value
    return [
        {
            type: "invest",
            label: t("kpis.investissement"),
            value: `${formatPvMetricValue(
                getNumericDataValue("prixHT_installation"),
                getFormatForDataKey("prixHT_installation")
            )} €`,
            subtitle: undefined as string | undefined,
            info: undefined as string | undefined,
            highlight: false,
            spanClass: "col-span-12 sm:col-span-6 lg:col-span-6 min-w-0",
        },
        {
            type: "bilan",
            label: t("kpis.bilan"),
            value: `${formatPvMetricValue(
                getNumericDataValue("cashflowHTactualise_sanscapex_sum"),
                getFormatForDataKey("cashflowHTactualise_sanscapex_sum")
            )} €`,
            subtitle: undefined as string | undefined,
            info: undefined as string | undefined,
            highlight: bilanHighlight,
            spanClass: "col-span-12 sm:col-span-6 lg:col-span-6 min-w-0",
        },
        {
            type: "van",
            label: t("kpis.van"),
            value: `${formatPvMetricValue(
                getNumericDataValue("van"),
                getFormatForDataKey("van")
            )} €`,
            subtitle: t("kpis.vanSub"),
            info: t("kpis.vanPopup"),
            highlight: false,
            spanClass: "col-span-12 sm:col-span-6 lg:col-span-3 min-w-0",
        },
        {
            type: "rsi",
            label: t("kpis.rsi"),
            value: `${formatPvMetricValue(
                getNumericDataValue("rsi"),
                getFormatForDataKey("rsi")
            )} %`,
            subtitle: t("kpis.rsiSub"),
            info: t("kpis.rsiPopup"),
            highlight: false,
            spanClass: "col-span-12 sm:col-span-6 lg:col-span-3 min-w-0",
        },
        {
            type: "tri",
            label: t("kpis.tri"),
            value: (() => {
                const v = getNumericDataValue("tri")
                if (v === undefined || v === null || Number.isNaN(Number(v))) {
                    return "- %"
                }
                return `${formatPvMetricValue(v, getFormatForDataKey("tri"))} %`
            })(),
            subtitle: t("kpis.triSub"),
            info: t("kpis.triPopup"),
            highlight: false,
            spanClass: "col-span-12 sm:col-span-6 lg:col-span-3 min-w-0",
        },
        {
            type: "payback",
            label: t("kpis.payback"),
            value: `${formatPvMetricValue(
                getNumericDataValue("duree_rentabilite"),
                getFormatForDataKey("duree_rentabilite")
            )} ans`,
            subtitle: t("kpis.paybackSub"),
            info: t("kpis.paybackPopup"),
            highlight: false,
            spanClass: "col-span-12 sm:col-span-6 lg:col-span-3 min-w-0",
        },
    ]
})

/** Données du tableau « impact financier » (valeurs déjà formatées + €). */
const getTableModel = computed((): EcoTableRow => {
    return {
        opex: formatTableMetricValue(
            "fluxHT_opex_avec_yearlymean",
            getNumericDataValue("fluxHT_opex_avec_yearlymean")
        ),
        elecsoutirage_sans_yearlymean: formatTableMetricValue(
            "fluxHT_elecsoutirage_tout_sans_yearlymean",
            getNumericDataValue("fluxHT_elecsoutirage_tout_sans_yearlymean")
        ),
        elecsoutirage_avec_yearlymean: formatTableMetricValue(
            "fluxHT_elecsoutirage_tout_avec_yearlymean",
            getNumericDataValue("fluxHT_elecsoutirage_tout_avec_yearlymean")
        ),
        diff_elect_tout_yearlymean: formatTableMetricValue(
            "cashflowHT_elecsoutirage_tout_yearlymean",
            Math.abs(
                Number(
                    getNumericDataValue(
                        "cashflowHT_elecsoutirage_tout_yearlymean"
                    ) ?? 0
                )
            )
        ),
        prixHT_elecinjection_avec_yearlymean: formatTableMetricValue(
            "fluxHT_elecinjection_tout_avec_yearlymean",
            getNumericDataValue("fluxHT_elecinjection_tout_avec_yearlymean")
        ),
        fluxHT_sans_yearlymean: formatTableMetricValue(
            "fluxHT_sanscapex_sans_yearlymean",
            getNumericDataValue("fluxHT_sanscapex_sans_yearlymean")
        ),
        fluxHT_avec_yearlymean: formatTableMetricValue(
            "fluxHT_sanscapex_avec_yearlymean",
            getNumericDataValue("fluxHT_sanscapex_avec_yearlymean")
        ),
        cashflowHT_yearlymean: formatTableMetricValue(
            "cashflowHT_sanscapex_yearlymean",
            getNumericDataValue("cashflowHT_sanscapex_yearlymean")
        ),
    }
})

/** Graphique cashflow (clé API legacy). */
const getCashflowGraph = computed(() =>
    getPlotlyGraphFromCharts(
        props.analyse.charts as unknown,
        "cashflowHTactualise"
    )
)

/** Graphique évolution facture / flux HT (clé API legacy). */
const getFluxHtGraph = computed(() =>
    getPlotlyGraphFromCharts(props.analyse.charts as unknown, "fluxHT")
)

/**
 * Adapte le layout Plotly pour qu’il suive le conteneur (autosize, sans hauteur
 * figée API) — complété par {@link PlotlyChart} via `Plots.resize` / `ResizeObserver`.
 *
 * @param graph - Payload Plotly brut
 */
function getPlotlyGraphFitContainer(
    graph: PlotlyGraphPayload | null
): PlotlyGraphPayload | null {
    if (!graph) return null
    const layout = { ...(graph.layout ?? {}) } as Record<string, unknown>
    layout.autosize = true
    delete layout.height
    delete layout.width
    return { data: graph.data, layout }
}

/** Cashflow prêt pour affichage dans une hauteur de carte définie. */
const getCashflowPlotGraph = computed(() =>
    getPlotlyGraphFitContainer(getCashflowGraph.value)
)

/** Flux HT prêt pour affichage dans une hauteur de carte définie. */
const getFluxHtPlotGraph = computed(() =>
    getPlotlyGraphFitContainer(getFluxHtGraph.value)
)

/**
 * Construit l'AOA "Synthèse" (KPIs financiers : valeurs numériques brutes, pas formatées,
 * pour permettre les calculs Excel).
 */
function buildKpisAoa(): unknown[][] {
    const kpiSpecs: {
        label: string
        dataKey: string
        unit: string
        subtitle?: string
    }[] = [
        {
            label: t("kpis.investissement"),
            dataKey: "prixHT_installation",
            unit: "€",
        },
        {
            label: t("kpis.bilan"),
            dataKey: "cashflowHTactualise_sanscapex_sum",
            unit: "€",
        },
        {
            label: t("kpis.van"),
            dataKey: "van",
            unit: "€",
            subtitle: t("kpis.vanSub"),
        },
        {
            label: t("kpis.rsi"),
            dataKey: "rsi",
            unit: "%",
            subtitle: t("kpis.rsiSub"),
        },
        {
            label: t("kpis.tri"),
            dataKey: "tri",
            unit: "%",
            subtitle: t("kpis.triSub"),
        },
        {
            label: t("kpis.payback"),
            dataKey: "duree_rentabilite",
            unit: "ans",
            subtitle: t("kpis.paybackSub"),
        },
    ]
    const rows: unknown[][] = [["Indicateur", "Valeur", "Unité", "Sous-titre"]]
    for (const spec of kpiSpecs) {
        const v = getNumericDataValue(spec.dataKey)
        rows.push([
            spec.label,
            v == null || Number.isNaN(Number(v)) ? null : Number(v),
            spec.unit,
            spec.subtitle ?? "",
        ])
    }
    return rows
}

/**
 * Construit l'AOA "Impact financier annuel" (valeurs numériques brutes, en €/an).
 * Reflète le contenu du tableau on-screen avec des libellés explicites.
 */
function buildImpactAoa(): unknown[][] {
    const impactSpecs: { label: string; dataKey: string; abs?: boolean }[] = [
        {
            label: "OPEX (provision onduleur inclus)",
            dataKey: "fluxHT_opex_avec_yearlymean",
        },
        {
            label: "Facture énergie soutirée — sans installation PV",
            dataKey: "fluxHT_elecsoutirage_tout_sans_yearlymean",
        },
        {
            label: "Facture énergie soutirée — avec installation PV",
            dataKey: "fluxHT_elecsoutirage_tout_avec_yearlymean",
        },
        {
            label: "Économies annuelles grâce à l'autoconsommation",
            dataKey: "cashflowHT_elecsoutirage_tout_yearlymean",
            abs: true,
        },
        {
            label: "Revenus annuels — vente du surplus injecté",
            dataKey: "fluxHT_elecinjection_tout_avec_yearlymean",
        },
        {
            label: "Bilan annuel — sans installation PV",
            dataKey: "fluxHT_sanscapex_sans_yearlymean",
        },
        {
            label: "Bilan annuel — avec installation PV",
            dataKey: "fluxHT_sanscapex_avec_yearlymean",
        },
        {
            label: "Cashflow annuel moyen (gain net)",
            dataKey: "cashflowHT_sanscapex_yearlymean",
        },
    ]
    const rows: unknown[][] = [["Métrique", "Valeur (€/an)"]]
    for (const spec of impactSpecs) {
        const raw = getNumericDataValue(spec.dataKey)
        let value: number | null = null
        if (raw != null && !Number.isNaN(Number(raw))) {
            value = spec.abs ? Math.abs(Number(raw)) : Number(raw)
        }
        rows.push([spec.label, value])
    }
    return rows
}

function onClickDownloadFinancialSynthesis(): void {
    downloadWorkbookAsExcel(
        [
            { kind: "rows", name: "Synthèse", rows: buildKpisAoa() },
            {
                kind: "rows",
                name: "Impact financier annuel",
                rows: buildImpactAoa(),
            },
            {
                kind: "chart",
                name: "Cashflow",
                graph: getCashflowPlotGraph.value,
                xAxisLabel: "Année",
            },
            {
                kind: "chart",
                name: "Évolution facture",
                graph: getFluxHtPlotGraph.value,
                xAxisLabel: "Année",
            },
        ],
        {
            chartSlug: "synthese-financiere",
            buildingSlug: props.analyse.name_analyse ?? props.analyse._id,
        }
    )
}
</script>

<template>
    <!-- ================================ -->
    <!-- Bloc principal — carte titrée (périmètre étude éco) -->
    <!-- ================================ -->
    <BaseTitledCard
        class="pv-etude-eco-display"
        content-class="!p-0"
        header-class="!text-left"
    >
        <template #header>
            <div
                class="flex md:flex-row flex-col md:items-center justify-between gap-3 w-full"
            >
                <div class="flex min-w-0 flex-1 flex-col gap-1">
                    <span class="text-lg font-semibold text-primary-800">
                        {{ getStudyHeading }}
                    </span>
                    <span class="text-sm font-normal text-primary-800/90">
                        {{ getDateLine }}
                    </span>
                </div>
                <div class="flex shrink-0 flex-wrap justify-end gap-2 ml-auto">
                    <BaseButton
                        :label="t('exportFinancialSynthesis')"
                        icon="pi pi-file-excel"
                        severity="secondary"
                        size="small"
                        :aria-label="t('exportFinancialSynthesis')"
                        @click="onClickDownloadFinancialSynthesis"
                    />
                    <BaseButton
                        :label="t('reportCta')"
                        icon="pi pi-file-pdf"
                        size="small"
                        :loading="isDownloadingReport"
                        :disabled="isDownloadingReport"
                        :aria-label="t('reportCta')"
                        @click="onClickDownloadReport"
                    />
                    <BaseButton
                        v-if="getShowDeleteEtudeAction"
                        v-tooltip.top="t('deleteTooltip')"
                        icon="pi pi-trash"
                        size="small"
                        text
                        rounded
                        severity="danger"
                        :loading="isDeletingEco"
                        :disabled="isDeletingEco"
                        :aria-label="t('deleteTooltip')"
                        @click="onClickOpenDeleteConfirm"
                    />
                </div>
            </div>
        </template>
        <template #content>
            <div
                class="flex flex-col px-4 py-5 sm:px-8 sm:py-6 gap-4 bg-[var(--p-content-background)]"
            >
                <!-- Aperçu financier — KPI -->
                <div
                    class="flex flex-wrap items-center justify-center gap-3 rounded-md bg-surface-100 px-4 py-3 text-center text-base font-medium text-primary-700"
                >
                    <span>{{ t("titles.overview") }}</span>
                    <i
                        v-tooltip.top="t('titles.overviewPopup')"
                        class="pi pi-info-circle cursor-help text-primary-600"
                        :aria-label="t('titles.overviewPopup')"
                    />
                </div>
                <div
                    class="grid grid-cols-12 gap-2 w-full"
                    :aria-label="t('titles.overview')"
                >
                    <div
                        v-for="kpi in getKpiItems"
                        :key="kpi.type"
                        :class="kpi.spanClass"
                    >
                        <BaseKPI
                            :type="kpi.type"
                            :label="kpi.label"
                            :value="kpi.value"
                            :clickable="false"
                            :highlight="kpi.highlight"
                            :pt="getEtudeEcoKpiPt(kpi.type)"
                        >
                            <template v-if="kpi.subtitle || kpi.info" #subtitle>
                                <div
                                    class="mt-1 flex flex-wrap items-center justify-center gap-1.5 text-center"
                                >
                                    <span
                                        v-if="kpi.subtitle"
                                        class="text-sm text-primary-600"
                                        >{{ kpi.subtitle }}</span
                                    >
                                    <BaseTooltipMessage
                                        v-if="kpi.info"
                                        :content="kpi.info"
                                        position="top"
                                        size="small"
                                        severity="info"
                                    />
                                </div>
                            </template>
                        </BaseKPI>
                    </div>
                </div>

                <BaseDivider class="my-4" />

                <!-- Tableau impact financier -->
                <div
                    class="flex flex-wrap items-center justify-center gap-3 rounded-md bg-surface-100 px-4 py-3 text-center text-base font-medium text-primary-700"
                >
                    <span>{{ t("titles.financialImpact") }}</span>
                    <i
                        v-tooltip.top="t('titles.financialImpactPopup')"
                        class="pi pi-info-circle cursor-help text-primary-600"
                        :aria-label="t('titles.financialImpactPopup')"
                    />
                </div>
                <div
                    class="w-full overflow-x-auto rounded-md border border-primary-200 bg-white"
                >
                    <table
                        class="w-full min-w-[640px] border-collapse text-left text-sm"
                    >
                        <thead>
                            <tr>
                                <th
                                    class="w-[20%] border border-primary-200 border-t-0 border-l-0 bg-[var(--p-content-background)] p-2 align-middle font-medium text-primary-950"
                                />
                                <th
                                    class="w-[20%] border border-primary-200 border-t-0 p-2 align-middle font-medium text-primary-950"
                                >
                                    {{ t("table.opex") }}
                                </th>
                                <th
                                    class="w-[20%] border border-primary-200 border-t-0 p-2 align-middle font-medium text-primary-950"
                                >
                                    {{ t("table.bill") }}
                                </th>
                                <th
                                    class="w-[20%] border border-primary-200 border-t-0 p-2 align-middle font-medium text-primary-950"
                                >
                                    {{ t("table.revenue") }}
                                </th>
                                <th
                                    class="w-[20%] border border-primary-200 border-t-0 border-r-0 p-2 align-middle font-medium text-primary-950"
                                >
                                    {{ t("table.bilan") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th
                                    class="sticky left-0 z-[1] w-[20%] border border-primary-200 border-l-0 bg-[var(--p-content-background)] p-2 align-middle text-xs font-medium text-primary-950"
                                >
                                    {{ t("table.consoWithoutPv") }}
                                </th>
                                <td
                                    class="border border-primary-200 p-2 text-gray-700"
                                >
                                    -
                                </td>
                                <td
                                    class="border border-primary-200 p-2 text-gray-700"
                                >
                                    {{
                                        getTableModel.elecsoutirage_sans_yearlymean
                                    }}
                                    €
                                </td>
                                <td
                                    class="border border-primary-200 p-2 text-gray-700"
                                >
                                    -
                                </td>
                                <td
                                    class="border border-l-primary-100 border-primary-200 border-r-0 p-2 text-gray-700"
                                >
                                    {{ getTableModel.fluxHT_sans_yearlymean }}
                                    €
                                </td>
                            </tr>
                            <tr>
                                <th
                                    class="sticky left-0 z-[1] w-[20%] border border-primary-200 border-l-0 bg-[var(--p-content-background)] p-2 align-middle text-xs font-medium text-primary-950"
                                >
                                    {{ t("table.consoWithPv") }}
                                </th>
                                <td
                                    class="border border-primary-200 border-b-primary-100 p-2 text-gray-700"
                                >
                                    {{ getTableModel.opex }} €
                                </td>
                                <td
                                    class="border border-primary-200 border-b-primary-100 p-2 text-gray-700"
                                >
                                    {{
                                        getTableModel.elecsoutirage_avec_yearlymean
                                    }}
                                    €
                                </td>
                                <td
                                    class="border border-primary-200 border-b-primary-100 p-2 text-gray-700"
                                >
                                    <p
                                        :class="
                                            getIsPositiveDisplay(
                                                getTableModel.prixHT_elecinjection_avec_yearlymean
                                            )
                                                ? 'text-emerald-600 font-medium'
                                                : ''
                                        "
                                    >
                                        {{
                                            getIsPositiveDisplay(
                                                getTableModel.prixHT_elecinjection_avec_yearlymean
                                            )
                                                ? "+"
                                                : ""
                                        }}{{
                                            getTableModel.prixHT_elecinjection_avec_yearlymean
                                        }}
                                        €
                                    </p>
                                </td>
                                <td
                                    class="border border-l-primary-100 border-primary-200 border-r-0 border-b-primary-100 p-2 text-gray-700"
                                >
                                    <p
                                        :class="
                                            getIsPositiveDisplay(
                                                getTableModel.fluxHT_avec_yearlymean
                                            )
                                                ? 'text-emerald-600 font-medium'
                                                : ''
                                        "
                                    >
                                        {{
                                            getIsPositiveDisplay(
                                                getTableModel.fluxHT_avec_yearlymean
                                            )
                                                ? "+"
                                                : ""
                                        }}{{
                                            getTableModel.fluxHT_avec_yearlymean
                                        }}
                                        €
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th
                                    class="sticky left-0 z-[1] w-[20%] border border-primary-200 border-b-0 border-l-0 border-t-primary-100 bg-[var(--p-content-background)] p-2 align-middle text-xs font-medium text-primary-950"
                                >
                                    {{ t("table.averageEconomy") }}
                                </th>
                                <td
                                    class="border border-primary-200 border-b-0 p-2 text-gray-700"
                                >
                                    -
                                </td>
                                <td
                                    class="border border-primary-200 border-b-0 p-2 text-gray-700"
                                >
                                    <p
                                        :class="
                                            getIsPositiveDisplay(
                                                getTableModel.diff_elect_tout_yearlymean
                                            )
                                                ? 'text-emerald-600 font-medium'
                                                : ''
                                        "
                                    >
                                        {{
                                            getIsPositiveDisplay(
                                                getTableModel.diff_elect_tout_yearlymean
                                            )
                                                ? "+"
                                                : ""
                                        }}{{
                                            getTableModel.diff_elect_tout_yearlymean
                                        }}
                                        €
                                    </p>
                                    <p class="text-xs text-gray-600 mt-1">
                                        {{ t("table.gainsAutoconso") }}
                                    </p>
                                </td>
                                <td
                                    class="border border-primary-200 border-b-0 p-2 text-gray-700"
                                >
                                    <p
                                        :class="
                                            getIsPositiveDisplay(
                                                getTableModel.prixHT_elecinjection_avec_yearlymean
                                            )
                                                ? 'text-emerald-600 font-medium'
                                                : ''
                                        "
                                    >
                                        {{
                                            getIsPositiveDisplay(
                                                getTableModel.prixHT_elecinjection_avec_yearlymean
                                            )
                                                ? "+"
                                                : ""
                                        }}{{
                                            getTableModel.prixHT_elecinjection_avec_yearlymean
                                        }}
                                        €
                                    </p>
                                    <p class="text-xs text-gray-600 mt-1">
                                        {{ t("table.gainsRevenue") }}
                                    </p>
                                </td>
                                <td
                                    class="border border-l-primary-100 border-primary-200 border-b-0 border-r-0 p-2 text-gray-700"
                                >
                                    <p
                                        :class="
                                            getIsPositiveDisplay(
                                                getTableModel.cashflowHT_yearlymean
                                            )
                                                ? 'text-emerald-600 font-medium'
                                                : ''
                                        "
                                    >
                                        {{
                                            getIsPositiveDisplay(
                                                getTableModel.cashflowHT_yearlymean
                                            )
                                                ? "+"
                                                : ""
                                        }}{{
                                            getTableModel.cashflowHT_yearlymean
                                        }}
                                        €
                                    </p>
                                    <p class="text-xs text-gray-600 mt-1">
                                        {{ t("table.gainsTotal") }}
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <BaseDivider class="my-4" />

                <!-- Cashflow -->
                <section
                    class="flex flex-col gap-4"
                    :aria-label="t('titles.cashflow')"
                >
                    <div
                        class="flex flex-wrap items-center justify-center gap-3 rounded-md bg-surface-100 px-4 py-3 text-center text-base font-medium text-primary-700"
                    >
                        <span>{{ t("titles.cashflow") }}</span>
                        <i
                            v-tooltip.top="t('titles.cashflowPopup')"
                            class="pi pi-info-circle cursor-help text-primary-600"
                            :aria-label="t('titles.cashflowPopup')"
                        />
                    </div>
                    <div
                        v-if="getCashflowPlotGraph"
                        class="box-border flex h-[32rem] min-h-[26rem] w-full shrink-0 flex-col rounded-md border border-gray-200 bg-white p-2"
                    >
                        <div class="min-h-0 min-w-0 flex-1">
                            <PlotlyChart :graph="getCashflowPlotGraph" />
                        </div>
                    </div>
                </section>

                <!-- Évolution facture (écart + séparation nette du graphique cashflow) -->
                <section
                    class="flex flex-col gap-4 border-t border-surface-200 pt-10 mt-10"
                    :aria-label="t('titles.evolutionBill')"
                >
                    <div
                        class="flex flex-wrap items-center justify-center gap-3 rounded-md bg-surface-100 px-4 py-3 text-center text-base font-medium text-primary-700"
                    >
                        <span>{{ t("titles.evolutionBill") }}</span>
                        <i
                            v-tooltip.top="t('titles.evolutionBillPopup')"
                            class="pi pi-info-circle cursor-help text-primary-600"
                            :aria-label="t('titles.evolutionBillPopup')"
                        />
                    </div>
                    <div
                        v-if="getFluxHtPlotGraph"
                        class="box-border flex h-[32rem] min-h-[26rem] w-full shrink-0 flex-col rounded-md border border-gray-200 bg-white p-2"
                    >
                        <div class="min-h-0 min-w-0 flex-1">
                            <PlotlyChart :graph="getFluxHtPlotGraph" />
                        </div>
                    </div>
                </section>
            </div>
        </template>
    </BaseTitledCard>
</template>
